import { Clock, InvalidDoc, noop, useIntl, useSettings, WarningRed } from '@capsicum/common';
import { BoldText, GenericDrawer, ModalService, useGoatImage, useRouter } from '@capsicum/common-web';
import { type ReactNode, useCallback } from 'react';
import styled from 'styled-components';

import { ProcessingModal } from '../ui/personal-verification/ProcessingModal';

import type { TranslationIDs } from '@capsicum/common';
import type { StaticImageData } from 'next/image';

type GenericModalProps = {
	body: string;
	buttonText?: string;
	image: StaticImageData;
	modalId: string;
	onButtonClick?: () => void;
	onDismiss?: () => void;
	title: string;
};

type ErrorModalProps = Omit<GenericModalProps, 'modalId' | 'onButtonClick'>;

type DocumentValidationModalProps = {
	description: TranslationIDs;
	image?: StaticImageData | string;
	onDismiss?: () => void;
	title: TranslationIDs;
};

export const useIdentityVerificationModals = () => {
	const { $t } = useIntl();
	const errorGoatImage = useGoatImage('error');
	const alertGoatImage = useGoatImage('alert');
	const { settings } = useSettings();
	const { back } = useRouter();

	const showGenericModal = useCallback(
		({ body, buttonText = '', image, modalId, onButtonClick, onDismiss, title }: GenericModalProps) => {
			ModalService.addModal({
				detached: true,
				isClosable: true,
				onDismiss,
				renderComponent: ({ close }) => (
					<GenericDrawer
						buttonText={buttonText}
						description={body}
						image={image}
						testID={`identity-verification-${modalId}-modal`}
						title={title}
						onButtonPress={() => {
							onButtonClick?.();
							close();
						}}
					/>
				)
			});
		},
		[]
	);

	const showDocumentValidationResultModal = useCallback(
		({ description, image = Clock, onDismiss = back, title }: DocumentValidationModalProps) => {
			ModalService.addModal({
				detached: true,
				onDismiss,
				renderComponent: () => (
					<GenericDrawer
						description={$t(description, {
							link: (chunks: Array<ReactNode>) => (
								<S.LinkText
									data-testid="identity-verification-document-validation-result-modal-link"
									href={settings?.contactUsHereLink as string}
								>
									{chunks}
								</S.LinkText>
							)
						})}
						image={image}
						testID="identity-verification-document-validation-result-modal"
						title={$t(title)}
					/>
				)
			});
		},
		[]
	);

	const showResultSuccessModal = (onDismiss?: () => void) =>
		showDocumentValidationResultModal({
			description: 'kycUserIdentity.successModal.description',
			onDismiss,
			title: 'kycUserIdentity.successModal.title'
		});

	const showResultErrorModal = (onDismiss?: () => void) =>
		showDocumentValidationResultModal({
			description: 'kycUserIdentity.errorModal.description',
			image: WarningRed,
			onDismiss,
			title: 'kycUserIdentity.errorModal.title'
		});

	const showWebviewErrorModal = (onDismiss?: () => void) =>
		showDocumentValidationResultModal({
			description: 'kycUserIdentity.webviewErrorModal.description',
			image: WarningRed,
			onDismiss,
			title: 'kycUserIdentity.errorModal.title'
		});

	const showErrorModal = useCallback(
		(props: ErrorModalProps) => {
			showGenericModal({
				...props,
				modalId: 'error',
				onDismiss: props.onDismiss ?? back
			});
		},
		[back, showGenericModal]
	);

	const showContactCustomerSupportModal = useCallback(
		(onDismiss = noop, title?: TranslationIDs) => {
			showGenericModal({
				body: $t<JSX.Element>('kycUserIdentity.retryModal.noMoreAttemptsDescription', {
					bold: chunks => <BoldText>{chunks}</BoldText>,
					link: chunks => (
						<S.LinkText
							data-testid="identity-verification-contact-customer-support-modal-link"
							href={settings?.contactUsHereLink as string}
						>
							{chunks}
						</S.LinkText>
					)
				}),
				image: errorGoatImage,
				modalId: 'contact-customer-support',
				onDismiss,
				title: $t(title || 'kycUserIdentity.retryModal.noMoreAttemptsTitle')
			});
		},
		[showGenericModal]
	);

	const showIdentityVerificationRejectedTileModal = useCallback(
		(onClick = noop) => {
			showGenericModal({
				body: $t('kycUserIdentity.retryModal.defaultDescription'),
				buttonText: $t('error.button.retry'),
				image: InvalidDoc,
				modalId: 'rejected-tile',
				onButtonClick: onClick,
				title: $t('kycUserIdentity.retryModal.defaultTitle')
			});
		},
		[showGenericModal]
	);

	const showUnderReviewModal = useCallback(
		(onDismiss = noop) => {
			ModalService.addModal({
				onDismiss,
				renderComponent: ({ close }) => (
					<ProcessingModal
						close={() => {
							close();
						}}
						testID="identity-verification-under-review-modal"
					/>
				)
			});
		},
		[showGenericModal]
	);
	const showIntroductoryModal = useCallback(
		(onClick = noop) => {
			showGenericModal({
				body: $t('kycUserIdentity.introductoryModal.description'),
				buttonText: $t('global.button.verify'),
				image: alertGoatImage,
				modalId: 'introductory',
				onButtonClick: onClick,
				title: $t('kycUserIdentity.introductoryModal.title')
			});
		},
		[showGenericModal]
	);

	const showExpiredVerificationModal = useCallback(
		(onClick = noop) => {
			showGenericModal({
				body: $t('kycUserIdentity.expiredModal.description'),
				buttonText: $t('kycUserIdentity.expiredModal.actionButton'),
				image: alertGoatImage,
				modalId: 'expired-verification',
				onButtonClick: onClick,
				title: $t('kycUserIdentity.expiredModal.title')
			});
		},
		[showGenericModal]
	);

	return {
		showContactCustomerSupportModal,
		showErrorModal,
		showExpiredVerificationModal,
		showIdentityVerificationRejectedTileModal,
		showIntroductoryModal,
		showResultErrorModal,
		showResultSuccessModal,
		showUnderReviewModal,
		showWebviewErrorModal
	};
};

namespace S {
	export const LinkText = styled.a`
		${({ theme }) => theme.fonts.text.text1};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		margin: 0;
	`;
}
